/**
 * Admin · Warranties list with search by registration / customer / id.
 *
 * Reuses the existing GET /api/warranty/registrations endpoint, which now
 * accepts an optional `q` parameter. Each row links to the customer-facing
 * warranty page so admins can preview what the customer sees.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Search, Shield, Loader2, ExternalLink, CheckCircle2, Clock, AlertTriangle, Pencil, Download } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from '../../../components/admin/billing/shared';
import EditWarrantyModal from '../../../components/admin/billing/EditWarrantyModal';
import { useInvoiceAccess, ViewOnlyTag } from '../../../lib/invoiceAccess';

const STATUS_BADGE = {
  approved: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
  pending_review: { label: 'Pending', cls: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700 border-red-300', icon: AlertTriangle },
  expired: { label: 'Expired', cls: 'bg-slate-100 text-slate-600 border-slate-300', icon: AlertTriangle },
};

const WarrantiesList = () => {
  const { authHeader } = useAuth();
  const { canEdit, viewOnly } = useInvoiceAccess();
  const [q, setQ] = useState('');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [verifyTpl, setVerifyTpl] = useState('');

  useEffect(() => {
    axios.get(`${API}/billing/settings/warranty-embed`, { headers: authHeader() })
      .then((r) => setVerifyTpl(r.data.verify_url_template || '')).catch(() => {});
  }, []); // eslint-disable-line
  const verifyUrl = (wid) => (verifyTpl ? verifyTpl.replace('__WID__', encodeURIComponent(wid)) : `/e-warranty?id=${encodeURIComponent(wid)}`);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
      const { data } = await axios.get(`${API}/warranty/registrations${params}`, { headers: authHeader() });
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      // Surface as much detail as possible to make production debugging easier.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
      console.error('[WarrantiesList] fetch failed:', status, detail, e);
      setErr(status ? `${status}: ${detail}` : formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [authHeader, q]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  return (
    <div className="p-4 sm:p-6 bg-slate-50 min-h-full space-y-4" data-testid="warranties-list-page">
      <div className="flex items-center gap-2">
        <Shield className="text-teal-600" size={22} />
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">eWarranties</h1>
        {viewOnly && <ViewOnlyTag />}
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by reg. number (KA01AB1234), customer, email, phone, or warranty ID…"
          className={`${inputCls} pl-9`}
          data-testid="warranties-search"
          autoFocus
        />
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">
            {q ? `No warranties matching "${q}".` : 'No warranty registrations yet.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2 font-semibold text-slate-600">Warranty ID</th>
                  <th className="px-4 py-2 font-semibold text-slate-600">Customer</th>
                  <th className="px-4 py-2 font-semibold text-slate-600">Vehicle</th>
                  <th className="px-4 py-2 font-semibold text-slate-600">Service</th>
                  <th className="px-4 py-2 font-semibold text-slate-600">Installed</th>
                  <th className="px-4 py-2 font-semibold text-slate-600">Expires</th>
                  <th className="px-4 py-2 font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((w) => {
                  const badge = STATUS_BADGE[w.status] || STATUS_BADGE.pending_review;
                  const Icon = badge.icon;
                  return (
                    <tr key={w.warranty_id} className="hover:bg-slate-50" data-testid={`warranty-row-${w.warranty_id}`}>
                      <td className="px-4 py-2 font-mono text-xs text-teal-700">{w.warranty_id}</td>
                      <td className="px-4 py-2">
                        <div className="font-medium text-slate-900">{w.customer_name || '—'}</div>
                        <div className="text-xs text-slate-500">{w.phone || w.email || '—'}</div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="text-slate-800">{[w.vehicle_make, w.vehicle_model, w.vehicle_year].filter(Boolean).join(' ') || '—'}</div>
                        {w.vehicle_registration && (
                          <div className="text-xs font-mono tracking-wider text-teal-700 mt-0.5" data-testid="warranty-row-reg">{w.vehicle_registration}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-slate-700 text-xs uppercase tracking-wider">{w.service_type || '—'}</td>
                      <td className="px-4 py-2 text-slate-600 text-xs">{fmtDate(w.installation_date)}</td>
                      <td className="px-4 py-2 text-slate-600 text-xs">{w.lifetime_warranty ? <span className="font-semibold text-teal-700">Lifetime</span> : fmtDate(w.warranty_expiry)}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider border px-2 py-0.5 rounded-full ${badge.cls}`}>
                          <Icon size={10} /> {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="inline-flex items-center gap-2">
                          {canEdit && (
                            <button
                              type="button"
                              onClick={() => setEditingId(w.warranty_id)}
                              className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-teal-700 hover:bg-teal-50 border border-slate-300 hover:border-teal-300 rounded px-2 py-1 transition"
                              data-testid={`warranty-edit-${w.warranty_id}`}
                              title="Edit warranty"
                            >
                              <Pencil size={12} /> Edit
                            </button>
                          )}
                          <a
                            href={`${API}/warranty/${encodeURIComponent(w.warranty_id)}/certificate`}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-teal-700"
                            data-testid={`warranty-certificate-${w.warranty_id}`}
                            title="Download the warranty certificate"
                          >
                            <Download size={12} /> Certificate
                          </a>
                          <a
                            href={verifyUrl(w.warranty_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-teal-700 hover:text-teal-800"
                            data-testid={`warranty-open-${w.warranty_id}`}
                            title="Open customer-facing warranty page"
                          >
                            <ExternalLink size={12} /> View
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500">
        {list.length > 0 && <>{list.length} warrant{list.length === 1 ? 'y' : 'ies'} {q && <>matching <b>{q}</b></>}</>}
      </p>

      {editingId && (
        <EditWarrantyModal
          warrantyId={editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); load(); }}
        />
      )}
    </div>
  );
};

export default WarrantiesList;
